import round from 'lodash/round';

import {
  Autocomplete,
  Icons,
  ListItem,
  ListItemText,
  Typography,
  Stack,
  tokens,
} from '@cast/design-system';
import type { SetState } from '@cast/shared-types';
import { OptimizationContainer } from '@cast/types';

import { getContainerResourceRecommendations } from 'features/workload-optimization';

const FormattedResourceMetrics = ({
  container,
}: {
  container: OptimizationContainer;
}) => {
  const { cpu, memory } = getContainerResourceRecommendations(container);
  return (
    <Stack direction="row" alignItems="center" whiteSpace="pre">
      {cpu.requested ? round(cpu.requested, 2) : 'N/A'}{' '}
      <Icons.ArrowRight css={{ marginRight: 3 }} />
      {cpu.recommended ? round(cpu.recommended, 2) : 'N/A'} CPU /{' '}
      {memory.requested ? round(memory.requested, 2) : 'N/A'}{' '}
      <Icons.ArrowRight css={{ marginRight: 3 }} />
      {memory.recommended ? round(memory.recommended, 2) : 'N/A'} GiB
    </Stack>
  );
};

type Props<T extends OptimizationContainer> = {
  selectedContainer: T;
  selectContainer: SetState<T>;
  containers: T[];
  showStartAdornment?: boolean;
  readOnly?: boolean;
};

export const ContainersDropdown = <T extends OptimizationContainer>({
  selectedContainer,
  selectContainer,
  containers,
  showStartAdornment = false,
  readOnly,
}: Props<T>) => {
  return (
    <Autocomplete<T, string>
      startAdornment={
        showStartAdornment ? (
          <Typography
            variant="P12R"
            borderRight="1px solid"
            borderColor={tokens.theme.border.structural.neutral}
            pr={6}
            mr={4}
          >
            Container selected:
          </Typography>
        ) : undefined
      }
      value={selectedContainer.name}
      onChange={selectContainer}
      options={containers}
      optionLabel={(option) => option.name}
      isOptionEqualToValue={({ name }, value) => name === value}
      renderValue={({ name }) => name}
      inputProps={{ readOnly: readOnly }}
      renderOption={(option, itemProps) => (
        <ListItem
          {...itemProps}
          endAdornment={
            <Typography variant="P12R">
              <FormattedResourceMetrics container={option} />
            </Typography>
          }
        >
          <ListItemText primary={option.name}></ListItemText>
        </ListItem>
      )}
      endAdornment={<FormattedResourceMetrics container={selectedContainer} />}
      size="sm"
      testId="containers-dropdown"
      sx={{
        '& svg': {
          display: readOnly ? 'none' : 'block',
        },
      }}
    />
  );
};
